import { useRef } from 'react';
import { Animated, GestureResponderHandlers, PanResponder } from 'react-native';
import { useSelector } from '@shared/store';
import { GESTURE_EXIT_VELOCITY } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';

export function useToastFling(
  onExit: () => void,
  translateX: Animated.Value,
): {
  panHandlers: GestureResponderHandlers | null;
  translateX: Animated.AnimatedValue;
} {
  const isDesktop = useIsDesktop();
  const viewportWidth = useSelector(state => state.appState.viewportWidth);
  const didFlingLeft = (vx: number): boolean => vx < -GESTURE_EXIT_VELOCITY;
  const didFlingRight = (vx: number): boolean => vx > GESTURE_EXIT_VELOCITY;
  const slideOutLeft = useRef(
    Animated.spring(translateX, { toValue: -viewportWidth, useNativeDriver: true }),
  ).current;
  const slideOutRight = useRef(
    Animated.spring(translateX, { toValue: viewportWidth, useNativeDriver: true }),
  ).current;
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => false,
      onMoveShouldSetPanResponder: (_event, gestureState) =>
        Math.abs(gestureState.dy) < 2 && Math.abs(gestureState.dx) >= 1,
      onPanResponderGrant: () => {
        translateX.stopAnimation();
        translateX.setOffset(0);
      },
      onPanResponderMove: (_event, gestureState) => translateX.setValue(gestureState.dx),
      onPanResponderRelease: (_event, gestureState) => {
        const { dx, vx } = gestureState;
        if (dx === 0 && vx === 0) {
          return;
        }
        translateX.flattenOffset();
        if (didFlingLeft(vx) || dx < -(viewportWidth / 2)) {
          slideOutLeft.start(onExit);
        } else if (didFlingRight(vx) || dx > viewportWidth / 2) {
          slideOutRight.start(onExit);
        } else {
          translateX.setValue(0);
        }
      },
    }),
  ).current;

  return { translateX, panHandlers: isDesktop ? null : panResponder.panHandlers };
}
